/**
 * Display names a player picks for themselves (docs/accounts/PLAN.md, "pick a name"). One
 * validator, run by the client as the player types and by the server as the only authority.
 *
 * Names are public (everyone at the table sees them) and NOT unique: guests never were, and a
 * uniqueness check would turn the picker into a guessing game. What this does guard:
 *
 *   - `length`   2–20 characters (code points) after normalizing.
 *   - `chars`    Unicode letters and digits, spaces, and `. _ - '`; at least one letter. Combining
 *                marks are allowed for the scripts that need them, but never two in a row, which
 *                is what stacked "zalgo" text is made of.
 *   - `reserved` anything that reads as the house: a `Guest-` name (that pattern is how the server
 *                knows a name was never picked), a bot persona, or a staff-sounding word.
 *   - `blocked`  a short word list (`displayNameBlocklist.ts`). A first line, not moderation.
 */
import { BLOCKED_NAME_PARTS } from './displayNameBlocklist.js';
export const DISPLAY_NAME_MIN = 2;
export const DISPLAY_NAME_MAX = 20;
/** The name every guest is born with (`server/auth/guest.ts`). Still matching it = never picked. */
export const GUEST_NAME_RE = /^Guest-[A-Z2-9]{4}$/;
/** True for an untouched `Guest-XXXX` name. */
export function isGuestName(name) {
    return GUEST_NAME_RE.test(name);
}
/**
 * The bot personas' names. Duplicated from `packages/bots/src/personas.ts` because `shared` may
 * not import `bots`; `server/__tests__/displayName.test.ts` fails if the two lists drift apart.
 */
export const BOT_PERSONA_NAMES = ['Unc', 'Auntie Ree', 'Deuce', 'Marcus', 'Tasha', 'Big Mike', 'Nia', 'Professor'];
/** Names that would read as the house or the staff, compared loosely (see `loose`). */
const RESERVED_WORDS = ['admin', 'moderator', 'spades night', 'dealer'];
/** Lower-case letters and digits only: `Big-Mike`, `big mike` and `BIGMIKE` compare equal. */
function loose(s) {
    return s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}
const RESERVED = new Set([...BOT_PERSONA_NAMES, ...RESERVED_WORDS].map(loose));
const LEET = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', $: 's' };
/** What the blocklist is matched against: accents stripped, leet undone, a–z only. */
export function squashForBlocklist(s) {
    return s
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[013457@$]/g, (c) => LEET[c] ?? c)
        .replace(/[^a-z]/g, '');
}
/** Trim, collapse runs of whitespace to one space, NFC. What gets stored when the name is valid. */
export function normalizeDisplayName(raw) {
    return raw.normalize('NFC').trim().replace(/\s+/gu, ' ');
}
export function validateDisplayName(raw) {
    if (typeof raw !== 'string')
        return { ok: false, reason: 'chars' };
    const name = normalizeDisplayName(raw);
    const length = [...name].length;
    if (length < DISPLAY_NAME_MIN || length > DISPLAY_NAME_MAX)
        return { ok: false, reason: 'length' };
    if (!/^[\p{L}\p{M}\p{N} ._'-]+$/u.test(name) || !/\p{L}/u.test(name) || /\p{M}{2}/u.test(name) || /^\p{M}/u.test(name)) {
        return { ok: false, reason: 'chars' };
    }
    if (/^guest-/i.test(name) || RESERVED.has(loose(name)))
        return { ok: false, reason: 'reserved' };
    const squashed = squashForBlocklist(name);
    if (BLOCKED_NAME_PARTS.some((part) => squashed.includes(part)))
        return { ok: false, reason: 'blocked' };
    return { ok: true, name };
}
//# sourceMappingURL=displayName.js.map